import { useState, useEffect, useRef, useCallback } from "react";
import { usePromptQueue, PromptPriority } from "@/contexts/PromptQueueContext";
import { MapPin, Navigation, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Mascot } from "@/components/Mascot";
import { getGeolocationErrorMessage } from "@/lib/locationDetection";
import {
  searchRegions,
  resolveRegionPrediction,
  detectCurrentLocation,
  isPermissionDenied,
  saveHomeLocation,
  type HomeLocation,
  type PlacePrediction,
} from "@/lib/homeLocation";
import { ONBOARDING_LOCATION_ANSWERED_KEY } from "@/lib/onboardingPending";
import { LocationPermissionDeniedModal } from "@/components/LocationPermissionDeniedModal";
import { getGeolocationPermissionState, type GeoPermissionState } from "@/lib/geolocationPermission";
import { LOCATION_COPY } from "@/lib/permissionCopy";

const DISMISSED_KEY = "grounded_location_prompt_dismissed";

export function LocationPromptDialog() {
  const { user } = useAuth();
  const { isAdmin } = useAdminRole(false);
  const { data: preferences, isLoading } = useUserPreferences();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();

  const PROMPT_ID = "location-prompt";
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [permissionDeniedOpen, setPermissionDeniedOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Only prime the ask while it is still unresolved. Someone who already
  // granted or denied sees no explanation they did not ask for.
  const [geoPermission, setGeoPermission] = useState<GeoPermissionState>("unknown");

  const ipGeoAttempted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!open) return;
    getGeolocationPermissionState().then((state) => {
      if (!cancelled) setGeoPermission(state);
    });
    return () => { cancelled = true; };
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    releasePrompt(PROMPT_ID);
  }, [releasePrompt]);

  useEffect(() => {
    if (isActivePrompt(PROMPT_ID)) {
      // Guard: if Browse already handled location, release immediately
      const browsePrompted =
        localStorage.getItem("grounded_browse_location_prompted") ||
        localStorage.getItem(ONBOARDING_LOCATION_ANSWERED_KEY);
      const browseLocation = localStorage.getItem("grounded_browse_location");
      if (browsePrompted || browseLocation) {
        releasePrompt(PROMPT_ID);
        return;
      }
      if (!open) setOpen(true);
    }
  }, [isActivePrompt, open, releasePrompt]);

  useEffect(() => {
    if (!user || isLoading || isAdmin) return;

    // Suppress on password recovery route or when recovery params are present
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const hash = window.location.hash || "";
      const query = window.location.search || "";
      if (path === "/reset-password" || hash.includes("type=recovery") || query.includes("type=recovery")) {
        return;
      }
    }

    // Only show on brand-new signup AND when no home location is saved yet.
    const justSignedUp = localStorage.getItem("grounded_just_signed_up") === "1";
    if (!justSignedUp) return;
    if (preferences?.home_city) {
      // Already has a home location — clear flag, never show.
      localStorage.removeItem("grounded_just_signed_up");
      return;
    }

    // Anyone who answered (or chose Not now on) the onboarding location step
    // already had their one location moment, so this never opens for them.
    const browseLocationPrompted =
      localStorage.getItem("grounded_browse_location_prompted") ||
      localStorage.getItem(ONBOARDING_LOCATION_ANSWERED_KEY);
    if (browseLocationPrompted) {
      localStorage.removeItem("grounded_just_signed_up");
      return;
    }

    // Brief delay to let auth/UI settle after signup.
    const timer = setTimeout(() => {
      const granted = requestPrompt(PROMPT_ID, PromptPriority.LOCATION);
      if (granted) {
        // Single-shot: clear flag the moment the modal is shown.
        localStorage.removeItem("grounded_just_signed_up");
        setOpen(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [user, preferences, isLoading, requestPrompt, isAdmin]);

  const handleDismiss = () => {
    localStorage.removeItem("grounded_just_signed_up");
    handleClose();
  };

  useEffect(() => {
    if (searchQuery.length < 2) {
      setPredictions([]);
      return;
    }

    const search = async () => {
      setIsSearching(true);
      try {
        setPredictions(await searchRegions(searchQuery));
      } catch {
        // silent
      } finally {
        setIsSearching(false);
      }
    };

    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const saveLocation = async (loc: HomeLocation) => {
    await saveHomeLocation(user!.id, loc);
    queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
    queryClient.invalidateQueries({ queryKey: ["feed"] });
  };

  const handleSelectPlace = async (prediction: PlacePrediction) => {
    setIsSaving(true);
    try {
      const loc = await resolveRegionPrediction(prediction);
      await saveLocation(loc);
      const { city, state } = loc;
      toast({ title: "Location set!", description: `You're in ${city}${state ? `, ${state}` : ""}` });
      handleClose();
    } catch {
      toast({ title: "Error saving location", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDetect = async () => {
    if (!navigator.geolocation) return;

    setIsDetecting(true);
    try {
      const loc = await detectCurrentLocation();
      await saveLocation(loc);
      const { city, state } = loc;
      toast({ title: "Location detected!", description: `Set to ${city}${state ? `, ${state}` : ""}` });
      handleClose();
    } catch (error) {
      if (isPermissionDenied(error)) {
        setPermissionDeniedOpen(true);
      } else {
        const { title, description } = getGeolocationErrorMessage(error);
        toast({ title, description, variant: "destructive" });
      }
    } finally {
      setIsDetecting(false);
    }
  };

  if (!user || isLoading || isAdmin) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <Mascot mood="happy" size="sm" />
          </div>
          <DialogTitle className="text-center">{LOCATION_COPY.heading}</DialogTitle>
          <DialogDescription className="text-center">
            {LOCATION_COPY.body}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div className="relative">
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by city, neighborhood, or zip..."
              className="pr-10"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {predictions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {predictions.map((p) => (
                  <button
                    key={p.place_id}
                    onClick={() => handleSelectPlace(p)}
                    disabled={isSaving}
                    className="w-full px-3 py-2 text-left hover:bg-accent transition-colors text-sm flex items-center gap-2"
                  >
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span>{p.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {geoPermission !== "denied" && (
            <>
              {geoPermission !== "granted" && (
                <p className="text-xs text-center text-muted-foreground px-2">
                  {LOCATION_COPY.why}
                </p>
              )}
              <Button variant="outline" size="sm" onClick={handleDetect} disabled={isDetecting} className="w-full">
                {isDetecting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Detecting...</>
                ) : (
                  <><Navigation className="h-4 w-4 mr-2" />{LOCATION_COPY.detectCta}</>
                )}
              </Button>
            </>
          )}

          <Button variant="ghost" size="sm" onClick={handleDismiss} className="w-full text-muted-foreground">
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <LocationPermissionDeniedModal
      open={permissionDeniedOpen}
      onClose={() => setPermissionDeniedOpen(false)}
      onSearchInstead={() => searchInputRef.current?.focus()}
    />
    </>
  );
}
